/**
 * useRoadmap – state and data loading for the Learning Roadmaps (tracks) feature.
 * Tracks are loaded from catalog/tracks; nodes for the selected track from tracks/{id}/nodes.
 * Optionally loads user progress (completedNodeIds) when userId is provided for node status coloring.
 *
 * Subscription: pass `options.allowedTrackIds` (array of trackId strings) to restrict the catalog
 * for the current user; omit or pass null to allow all published tracks.
 *
 * Pass `options.roadmapSync` with URL/menu primitives so graph language resolves in one memo (no
 * competing useEffects syncing `selectedLanguage`). Optional `preferPublicFundamentalsTrack`
 * picks Programming Fundamentals first for auto-selection (pair with RoadmapView free-tier guard).
 * Optional `constrainSelectionToLanguage` (default true): when false (role Learn paths), keep
 * cross-language track selections instead of resetting to the URL language's Fundamentals track.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as roadmapService from '../services/roadmapService';
import { buildOrderedNodeGraph } from '../utils/roadmapGraph';
import { getTrackLanguageKey, trackMatchesLanguage, normalizeRoadmapLanguageKey } from '../utils/roadmapDisplay';
import { LAST_ROADMAP_LANG_KEY } from '../utils/roadmapLanguageMenuStats';
import { isRoadmapFreePublicTrack } from '../utils/roadmapSubscription';

/** Stable empty ref so `availableLanguages` is not a new [] every render (avoids roadmap effect churn). */
const AVAILABLE_LANGUAGES_EMPTY = [];

/** Default selection within a language-filtered track list (free tier: avoid null ↔ paywalled track oscillation). */
function pickDefaultTrackForLanguage(list, preferPublicFundamentals) {
  if (!list?.length) return null;
  if (preferPublicFundamentals) {
    const fund = list.find((t) => isRoadmapFreePublicTrack(t));
    if (fund) return fund;
  }
  return list[0];
}

/**
 * @typedef {{
 *   showLanguageMenu: boolean,
 *   isGraphRoute: boolean,
 *   graphSearchLangNorm: string,
 *   graphNavMenuLangNorm: string
 * }} RoadmapSyncInput
 */

/**
 * @param {string|null} userId
 * @param {{ allowedTrackIds?: string[]|null, roadmapSync?: RoadmapSyncInput|null, preferPublicFundamentalsTrack?: boolean, constrainSelectionToLanguage?: boolean }} [options]
 */
export function useRoadmap(userId = null, options = {}) {
  const {
    allowedTrackIds = null,
    roadmapSync = null,
    preferPublicFundamentalsTrack = false,
    constrainSelectionToLanguage = true
  } = options;
  const [tracks, setTracks] = useState([]);
  const tracksRef = useRef(tracks);
  tracksRef.current = tracks;

  /** Content fingerprint so memos/effects do not churn when `tracks` gets a new array reference with the same catalog. */
  const tracksCatalogSig =
    tracks.length === 0
      ? ''
      : tracks
          .map((t) => `${String(t.trackId)}:${getTrackLanguageKey(t)}`)
          .sort()
          .join('|');
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [trackProgress, setTrackProgress] = useState({ completedNodeIds: [], nodeScores: {}, nodeLastAttempts: {} });
  const [isLoadingTracks, setIsLoadingTracks] = useState(true);
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [error, setError] = useState(null);

  const loadTracks = useCallback(async () => {
    setIsLoadingTracks(true);
    setError(null);
    try {
      const list = await roadmapService.loadPublishedTracks();
      setTracks(list);
    } catch (err) {
      console.error('Failed to load tracks:', err);
      setError(err.message || 'Failed to load roadmaps');
      setTracks([]);
    } finally {
      setIsLoadingTracks(false);
    }
  }, []);

  const loadNodesForTrack = useCallback(async (trackId) => {
    if (!trackId) {
      setNodes([]);
      return;
    }
    setIsLoadingNodes(true);
    setError(null);
    try {
      const list = await roadmapService.loadTrackNodes(trackId);
      setNodes(list);
    } catch (err) {
      console.error('Failed to load track nodes:', err);
      setError(err.message || 'Failed to load track');
      setNodes([]);
    } finally {
      setIsLoadingNodes(false);
    }
  }, []);

  const selectTrack = useCallback((track) => {
    setSelectedTrack(track);
    if (!track) setNodes((prev) => (prev.length === 0 ? prev : []));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedTrack(null);
    setNodes((prev) => (prev.length === 0 ? prev : []));
  }, []);

  useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  useEffect(() => {
    if (selectedTrack) {
      loadNodesForTrack(selectedTrack.trackId);
    } else {
      setNodes((prev) => (prev.length === 0 ? prev : []));
    }
  }, [selectedTrack?.trackId, loadNodesForTrack]);

  const orderedNodes = useMemo(() => buildOrderedNodeGraph(nodes), [nodes]);

  const nodeById = useMemo(() => {
    const map = new Map();
    nodes.forEach((n) => map.set(n.id, n));
    return map;
  }, [nodes]);

  const completedNodeIds = useMemo(() => trackProgress.completedNodeIds || [], [trackProgress]);
  const nodeScores = useMemo(() => trackProgress.nodeScores || {}, [trackProgress]);
  const nodeLastAttempts = useMemo(
    () => trackProgress.nodeLastAttempts || {},
    [trackProgress]
  );

  /** First node in topological order that is not completed = current node to complete (yellow). */
  const currentNodeId = useMemo(() => {
    const set = new Set(completedNodeIds);
    const first = orderedNodes.find((n) => !set.has(n.id));
    return first ? first.id : null;
  }, [orderedNodes, completedNodeIds]);

  /** All nodes in the track graph are unlocked for interaction; order is still orderedNodes. */
  const unlockedNodeIds = useMemo(() => {
    const set = new Set();
    orderedNodes.forEach((n) => set.add(n.id));
    return set;
  }, [orderedNodes]);

  useEffect(() => {
    if (!userId || !selectedTrack?.trackId) {
      if (!userId) setTrackProgress({ completedNodeIds: [], nodeScores: {}, nodeLastAttempts: {} });
      return;
    }
    const trackId = selectedTrack.trackId;
    roadmapService.loadTrackProgress(userId, trackId).then((data) => setTrackProgress(data)).catch(() => { });
    const unsubscribe = roadmapService.subscribeToTrackProgress(
      userId,
      trackId,
      (data) => setTrackProgress(data)
    );
    return () => unsubscribe();
  }, [userId, selectedTrack?.trackId]);

  const [selectedLanguage, setSelectedLanguageState] = useState('all');

  const availableLanguages = useMemo(() => {
    const set = new Set();
    tracksRef.current.forEach((t) => {
      const k = getTrackLanguageKey(t);
      if (k) set.add(k);
    });
    const list = Array.from(set).sort();
    return list.length ? list : AVAILABLE_LANGUAGES_EMPTY;
  }, [tracksCatalogSig]);

  const preferredLanguageFromStorage = useMemo(() => {
    if (!userId || !tracksCatalogSig) return null;
    try {
      const k = LAST_ROADMAP_LANG_KEY(userId);
      if (!k) return null;
      const stored = window.localStorage.getItem(k);
      if (!stored) return null;
      const sn = normalizeRoadmapLanguageKey(stored);
      return availableLanguages.find((l) => normalizeRoadmapLanguageKey(l) === sn) || null;
    } catch {
      return null;
    }
  }, [userId, tracksCatalogSig, availableLanguages]);

  /**
   * Single derived language for track lists on the graph route: URL → nav state → localStorage → first catalog lang.
   * On the language menu route, uses `selectedLanguage` (typically `all`).
   */
  const languageForTracks = useMemo(() => {
    if (!roadmapSync) return selectedLanguage;
    const {
      showLanguageMenu,
      isGraphRoute,
      graphSearchLangNorm = '',
      graphNavMenuLangNorm = ''
    } = roadmapSync;
    if (showLanguageMenu) return selectedLanguage;
    if (!isGraphRoute) return selectedLanguage;
    if (!tracksCatalogSig) return selectedLanguage;
    if (!availableLanguages.length) return selectedLanguage;

    const fromSearch =
      graphSearchLangNorm &&
      availableLanguages.find((l) => normalizeRoadmapLanguageKey(l) === graphSearchLangNorm);
    const fromNav =
      graphNavMenuLangNorm &&
      availableLanguages.find((l) => normalizeRoadmapLanguageKey(l) === graphNavMenuLangNorm);

    return (
      fromSearch ||
      fromNav ||
      preferredLanguageFromStorage ||
      availableLanguages[0] ||
      selectedLanguage
    );
  }, [
    roadmapSync?.showLanguageMenu,
    roadmapSync?.isGraphRoute,
    roadmapSync?.graphSearchLangNorm,
    roadmapSync?.graphNavMenuLangNorm,
    tracksCatalogSig,
    selectedLanguage,
    preferredLanguageFromStorage,
    availableLanguages
  ]);

  const persistLangNorm = normalizeRoadmapLanguageKey(String(languageForTracks || ''));

  useEffect(() => {
    if (!userId || typeof window === 'undefined') return;
    if (!roadmapSync?.isGraphRoute || roadmapSync?.showLanguageMenu) return;
    if (!persistLangNorm || languageForTracks === 'all') return;
    const k = LAST_ROADMAP_LANG_KEY(userId);
    if (!k) return;
    try {
      window.localStorage.setItem(k, languageForTracks);
    } catch {
      /* ignore */
    }
  }, [userId, roadmapSync?.isGraphRoute, roadmapSync?.showLanguageMenu, persistLangNorm, languageForTracks]);

  const allowedSet = useMemo(() => {
    if (!allowedTrackIds || allowedTrackIds.length === 0) return null;
    return new Set(allowedTrackIds.map((id) => String(id)));
  }, [allowedTrackIds]);

  const tracksForLanguage = useMemo(() => {
    const tr = tracksRef.current;
    const lang = languageForTracks;
    let list =
      !lang || lang === 'all'
        ? [...tr]
        : tr.filter((t) => trackMatchesLanguage(t, lang));
    if (allowedSet) {
      list = list.filter((t) => allowedSet.has(String(t.trackId)));
    }
    return list;
  }, [tracksCatalogSig, languageForTracks, allowedSet]);

  const setSelectedLanguage = useCallback((lang) => {
    setSelectedLanguageState(lang);
  }, []);

  useEffect(() => {
    // Role paths are cross-language — do not auto-pick a language-scoped Fundamentals track.
    if (!constrainSelectionToLanguage) return;
    const lang = languageForTracks;
    if (!lang || lang === 'all') return;
    const tr = tracksRef.current;
    let list = tr.filter((t) => trackMatchesLanguage(t, lang));
    if (allowedSet) list = list.filter((t) => allowedSet.has(String(t.trackId)));
    if (selectedTrack || list.length === 0) return;
    const pick = pickDefaultTrackForLanguage(list, preferPublicFundamentalsTrack);
    if (pick) selectTrack(pick);
  }, [
    tracksCatalogSig,
    selectedTrack,
    selectTrack,
    languageForTracks,
    allowedSet,
    preferPublicFundamentalsTrack,
    constrainSelectionToLanguage
  ]);

  useEffect(() => {
    if (!selectedTrack) return;
    const tr = tracksRef.current;
    let list;
    if (!constrainSelectionToLanguage) {
      // Role Learn: selection may be any catalog track (or allowed set), not URL language.
      list = allowedSet
        ? tr.filter((t) => allowedSet.has(String(t.trackId)))
        : [...tr];
    } else {
      const lang = languageForTracks;
      list =
        !lang || lang === 'all'
          ? [...tr]
          : tr.filter((t) => trackMatchesLanguage(t, lang));
      if (allowedSet) list = list.filter((t) => allowedSet.has(String(t.trackId)));
    }
    if (list.length === 0) return;
    const stillInList = list.some((t) => t.trackId === selectedTrack.trackId);
    if (!stillInList) {
      const pick = constrainSelectionToLanguage
        ? pickDefaultTrackForLanguage(list, preferPublicFundamentalsTrack)
        : list[0] || null;
      selectTrack(pick || null);
    }
  }, [
    tracksCatalogSig,
    languageForTracks,
    selectedTrack,
    selectTrack,
    allowedSet,
    preferPublicFundamentalsTrack,
    constrainSelectionToLanguage
  ]);

  useEffect(() => {
    if (!allowedSet || !selectedTrack) return;
    if (!allowedSet.has(String(selectedTrack.trackId))) {
      const tr = tracksRef.current;
      let list;
      if (!constrainSelectionToLanguage) {
        list = tr.filter((t) => allowedSet.has(String(t.trackId)));
      } else {
        const lang = languageForTracks;
        list =
          !lang || lang === 'all'
            ? [...tr]
            : tr.filter((t) => trackMatchesLanguage(t, lang));
        list = list.filter((t) => allowedSet.has(String(t.trackId)));
      }
      const pick = constrainSelectionToLanguage
        ? pickDefaultTrackForLanguage(list, preferPublicFundamentalsTrack)
        : list[0] || null;
      selectTrack(pick || null);
    }
  }, [
    allowedSet,
    selectedTrack,
    selectTrack,
    tracksCatalogSig,
    languageForTracks,
    preferPublicFundamentalsTrack,
    constrainSelectionToLanguage
  ]);

  return {
    tracks,
    selectedTrack,
    nodes,
    orderedNodes,
    nodeById,
    completedNodeIds,
    nodeScores,
    nodeLastAttempts,
    currentNodeId,
    unlockedNodeIds,
    availableLanguages,
    tracksForLanguage,
    selectedLanguage,
    setSelectedLanguage,
    languageForTracks,
    tracksCatalogSig,
    isLoadingTracks,
    isLoadingNodes,
    error,
    loadTracks,
    loadNodesForTrack,
    selectTrack,
    clearSelection
  };
}
